import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { iAssemblyModel, iAssemblyModelState, CalculationProcessStep, Co2ReferenceObject, AssemblyCo2ReferenceObject } from 'store/types/assembler';


// Maps "category.subCategory" to a canonical context string
const ALLOWED_CONTEXTS_BY_TARGET: Record<string, string[]> = {
  // Materials
  'materials.metals':       ['.materials.metals'],
  'materials.polymers':     ['.materials.polymers'],
  'materials.composites':   ['.materials.composites'],
  'materials.sinters':      ['.materials.sinters'],
  'materials.elements':     ['.materials.elements'],
  'materials.other':        ['.materials.other'],

  // Logistics
  'logistics.transportation': ['.logistics.transportation'],
  'logistics.packaging':      ['.logistics.packaging'],

  // Components
  'components.electronics': ['.electronics'],

  // Production
  'production.manufacturing': ['.production.manufacturing'], // keep as-is if you have one
  // 👇 KEY CHANGE: energy accepts BOTH country- and carrier-based datasets
  'production.energy':      ['.energy.countries', '.energy.carriers'],
}

const ALLOWED_PARAMS: Record<string, readonly string[]> = {
  // Materials
  'materials.metals':       ['netWeight','countryCode','yield','recyclingRatio','shape','customFootprint'],
  'materials.polymers':     ['netWeight','countryCode','yield','customFootprint'],
  'materials.composites':   ['netWeight','countryCode','yield','recyclingRatio','customFootprint'],
  'materials.sinters':      ['netWeight','countryCode','yield','customFootprint'],
  'materials.elements':     ['netWeight','countryCode','yield','customFootprint'],
  'materials.other':        ['netWeight','countryCode','yield','customFootprint'],

  // Logistics
  'logistics.transportation': ['distance','transportGrossWeight','partDensity','customFootprint'],
  'logistics.packaging':      ['netWeight','customFootprint'],

  // Components
  'electronics': ['netWeight','countryCode','customFootprint'],

  // Production
  'production.manufacturing': ['consumption','countryCode','customFootprint'],
  'production.energy':        ['consumption','countryCode','customFootprint'], // countryCode optional
  'components.electronics':   ['netWeight','countryCode','customFootprint'],
}

const targetKey = (category: string, subCategory: string) =>
  `${category}.${subCategory}`

function sanitizeParamsForTarget(
  params: Record<string, any> | undefined,
  category: string,
  subCategory: string,
  dropCountryAlwaysForCustom = false
) {
  const key = targetKey(category, subCategory)
  const allowed = new Set(ALLOWED_PARAMS[key] || [])
  const out: Record<string, any> = {}
  if (params) for (const k of Object.keys(params)) if (allowed.has(k)) out[k] = (params as any)[k]
  if (dropCountryAlwaysForCustom) { delete out.countryCode; delete out.countryName }
  return out
}

function normalizeItemForSource(
  item: AssemblyCo2ReferenceObject,
  targetSource: 'sustamize' | 'collector' | 'custom',
  targetCategory: 'materials' | 'logistics' | 'production' | 'components',
  subCategory: string
): Co2ReferenceObject | any {
  // Special-case: Production Line stays as before (unchanged)
  if (subCategory === 'Production Line' && targetSource === 'custom') {
    const energy = (item.energy ?? []).map(({ energyType, ...rest }: any) => rest)
    const employees = (item.employees ?? []).map(({ energyType, calculatedEntityEmission, entityEmissionReference, source, ...rest }: any) => rest)
    const equipment = (item.equipment ?? []).map(({ energyType, calculatedEntityEmission, entityEmissionReference, source, ...rest }: any) => rest)
    return { name: item.name, comment: item.comment, baseParams: item.baseParams, employees, equipment, energy }
  }

  const key = targetKey(targetCategory, subCategory)
  const allowedContexts = ALLOWED_CONTEXTS_BY_TARGET[key]
  // Choose context:
  // - if current item.context is allowed → keep it (and keep sid)
  // - else pick the first allowed as default and clear sid
  let effectiveContext = item.context
  let clearSid = false
  if (allowedContexts && allowedContexts.length) {
    if (!item.context || !allowedContexts.includes(item.context)) {
      effectiveContext = allowedContexts[0]
      clearSid = true
    }
  }
  // If no allowed list, keep context as-is and keep sid

  if (targetSource === 'sustamize' || targetSource === 'collector') {
    const pruned = sanitizeParamsForTarget(item.params as any, targetCategory, subCategory)
    return {
      name: item.name,
      sid: clearSid ? undefined : item.sid,
      context: effectiveContext,
      emissionType: item.emissionType,
      params: pruned,
      calculatedEntityEmission: item.calculatedEntityEmission,
      entityEmissionReference: item.entityEmissionReference,
      unit: item.unit,
    } as Co2ReferenceObject
  }

  // custom
  const custom = sanitizeParamsForTarget(item.params as any, targetCategory, subCategory, true)
  return {
    name: item.name,
    comment: item.comment,
    context: effectiveContext,
    emissionType: item.emissionType,
    unit: item.unit,
    params: custom,
    calculatedEntityEmission: item.calculatedEntityEmission,
    entityEmissionReference: item.entityEmissionReference,
  } as Co2ReferenceObject
}

export const initialState: iAssemblyModelState = {
  //assemblyModel: assemblerBuild as iAssemblyModel,
  assemblyModel: {
    id: '',
    name: '',
    category: '',
    assemblyModel: {
    calculationProcessChain: []
  }
  },
};

export const assemblyModelSlice = createSlice({
  name: 'assemblyModel',
  initialState,
  reducers: {
    // Replace the entire assembly state
    setassemblyModel: (state, action: PayloadAction<iAssemblyModel>) => {
      const incoming = action.payload;
      // 1) Set the top-level name
      state.assemblyModel.name = incoming.name ?? '';
      state.assemblyModel.id = incoming.id ?? '';

      const model = incoming?.assemblyModel ?? { calculationProcessChain: [] };
      const chain = (model as any).calculationProcessChain ?? [];

      // Ensure legacy nested buckets exist so CollapsibleTable / Co2Calculator keep working
      // when loading flat CQS models (targets-only) from create / migrated data.
      for (const step of chain) {
        if (!step.sustamizeTargets) {
          step.sustamizeTargets = {
            materials: {},
            logistics: {},
            components: {},
            production: {},
          };
        }
        if (!step.customTargets) {
          step.customTargets = {
            materials: {},
            components: {},
            logistics: {},
            production: {},
            productionLine: {},
          };
        }
        if (!Array.isArray(step.targets)) {
          step.targets = [];
        }
      }

      state.assemblyModel.assemblyModel = model as iAssemblyModel['assemblyModel'];
    },

    /**
     * 1) ADD a new calculation step
     * Payload can include name, or any data you want for the new step
     */
    addCalculationProcessStep: (state, action: PayloadAction<{ stepName: string }>) => {
      if (!state.assemblyModel.assemblyModel.calculationProcessChain) {
        state.assemblyModel.assemblyModel.calculationProcessChain = [];
      }
      // Create a new step object
      const newStep: CalculationProcessStep = {
        name: action.payload.stepName,
        position: state.assemblyModel.assemblyModel.calculationProcessChain.length + 1,
        subAssemblies: [],
        // Flat CQS edit model (preferred for create/calculate). Nested buckets kept
        // for legacy CollapsibleTable / Co2Calculator until fully migrated.
        targets: [],
        sustamizeTargets: {
          materials: {},
          logistics: {},
          components: {},
          production: {},
        },
        customTargets: {
          materials: {},
          components: {},
          logistics: {},
          production: {},
          productionLine: {}
        },
      } as CalculationProcessStep;

      // Add the new step to the array
      state.assemblyModel.assemblyModel.calculationProcessChain.push(newStep);
    },

    renameCalculationProcessStep: (
      state,
      action: PayloadAction<{ stepIndex: number; newName: string }>
    ) => {
      const { stepIndex, newName } = action.payload
      const step = state.assemblyModel.assemblyModel.calculationProcessChain[stepIndex]
      if (step) {
        step.name = newName
      }
    },

    addCalculationProcessStepFromTemplate: (
      state,
      action: PayloadAction<CalculationProcessStep>
    ) => {
      const newStep = action.payload
      newStep.position =
        state.assemblyModel.assemblyModel.calculationProcessChain.length + 1

      state.assemblyModel.assemblyModel.calculationProcessChain.push(newStep)
    },

    /**
     * 2) DELETE a calculation step by index or name
     * Here we do it by index. If you prefer by ID, 
     * adapt the payload and filter the array accordingly.
     */
    deleteCalculationProcessStep: (state, action: PayloadAction<number>) => {
      const stepIndex = action.payload;
      // Filter out the step at stepIndex
      state.assemblyModel.assemblyModel.calculationProcessChain = state.assemblyModel.assemblyModel.calculationProcessChain.filter(
        (_step, idx) => idx !== stepIndex
      );
      // If you need to reassign .position, do so here
      // e.g., re-index them 1..N
      state.assemblyModel.assemblyModel.calculationProcessChain.forEach((step, i) => {
        step.position = i + 1;
      });
    },
addCategoryItem: (
  state,
  action: PayloadAction<{
    stepIndex: number;
    category: 'materials' | 'logistics' | 'production' | 'components';
    subCategory: string;
    source: 'sustamize' | 'collector' | 'custom';
    item: AssemblyCo2ReferenceObject;
  }>
) => {
  const { stepIndex, category, subCategory, source, item } = action.payload;

  const step = state.assemblyModel.assemblyModel.calculationProcessChain[stepIndex];
  if (!step) return;

  // ✅ Ensure base targets exist before accessing their properties
  step.sustamizeTargets = step.sustamizeTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  };

  step.customTargets = step.customTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  };

  // ✅ Select correct target object (now TypeScript knows both exist)
  let targets =
    source === 'sustamize' || source === 'collector'
      ? step.sustamizeTargets[category]
      : step.customTargets[category];

  // ✅ Ensure category container exists (e.g., step.sustamizeTargets.materials)
  if (!targets) {
    const emptyCategory: Record<string, Co2ReferenceObject[]> = {};
    if (source === 'sustamize' || source === 'collector') {
      step.sustamizeTargets[category] = emptyCategory;
      targets = step.sustamizeTargets[category];
    } else {
      step.customTargets[category] = emptyCategory;
      targets = step.customTargets[category];
    }
  }

  // ✅ Safely cast for flexible indexing
  const finalTargets = targets as Record<string, Co2ReferenceObject[]>;

  // ✅ Ensure subcategory array exists
  if (!finalTargets[subCategory]) {
    finalTargets[subCategory] = [];
  }

  // ✅ Prepare the item
  let finalItem: Partial<Co2ReferenceObject> = {};

  if (source === 'sustamize') {
    finalItem = {
      name: item.name,
      sid: item.sid,
      context: item.context,
      emissionType: item.emissionType,
      params: item.params,
      calculatedEntityEmission: item.calculatedEntityEmission,
      entityEmissionReference: item.entityEmissionReference,
    };
  } else if (source === 'custom') {
    const customParams = { ...item.params } as Record<string, any>;
    delete customParams.countryCode;

    finalItem = {
      name: item.name,
      comment: item.comment,
      context: item.context,
      emissionType: item.emissionType,
      unit: item.unit,
      params: customParams,
      calculatedEntityEmission: item.calculatedEntityEmission,
      entityEmissionReference: item.entityEmissionReference,
    };
  } else if (subCategory === 'Production Line') {
    // Clean up energy items
    item?.energy?.forEach((energyItem: any) => {
      delete energyItem.energyType;
    });

    finalItem = {
      name: item.name,
      comment: item.comment,
      baseParams: item.baseParams,
      employees: item.employees,
      equipment: item.equipment,
      energy: item.energy,
    };
  }

  // ✅ Push or assign depending on the case
  if (source === 'custom' && subCategory === 'Production Line') {
    step.customTargets.production = finalItem as any;
  } else {
    finalTargets[subCategory].push(finalItem as Co2ReferenceObject);
  }
},


    /**
     * REMOVE a category item from a given step
     */
removeCategoryItem: (
  state,
  action: PayloadAction<{
    stepIndex: number
    category: 'materials' | 'logistics' | 'production' | 'components'
    subCategory: string
    source: 'sustamize' | 'collector' | 'custom'
    itemIndex: number // position in the subCategory array
  }>
) => {
  const { stepIndex, category, subCategory, source, itemIndex } = action.payload
  const step =
    state.assemblyModel.assemblyModel.calculationProcessChain[stepIndex]
  if (!step) return

  // ✅ Ensure target containers exist — avoids undefined issues
  step.sustamizeTargets = step.sustamizeTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  }
  step.customTargets = step.customTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  }

  // ✅ Select the correct target set
  const targets =
    source === 'sustamize' || source === 'collector'
      ? step.sustamizeTargets[category]
      : step.customTargets[category]

  // ✅ Handle special case for custom production
  if (
    category === 'production' &&
    source === 'custom' &&
    subCategory === 'productionItem'
  ) {
    step.customTargets.production = {}
    return
  }

  // ✅ Safely cast + guard
  const finalTargets = (targets ?? {}) as Record<string, Co2ReferenceObject[]>

  if (!finalTargets[subCategory]) return

  // ✅ Remove the specified item safely
  finalTargets[subCategory].splice(itemIndex, 1)
},


updateCategoryItem: (
  state,
  action: PayloadAction<{
    stepIndex: number
    category: 'materials' | 'logistics' | 'production' | 'components'
    subCategory: string
    source: 'sustamize' | 'collector' | 'custom'
    itemIndex: number
    item: AssemblyCo2ReferenceObject
  }>
) => {
  const { stepIndex, category, subCategory, source, itemIndex, item } =
    action.payload

  const step =
    state.assemblyModel.assemblyModel.calculationProcessChain[stepIndex]
  if (!step) return

  // ✅ Ensure targets exist (avoids undefined access)
  step.sustamizeTargets = step.sustamizeTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  }
  step.customTargets = step.customTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  }

  // ✅ Safely pick correct target set
  const targets =
    source === 'sustamize' || source === 'collector'
      ? step.sustamizeTargets[category]
      : step.customTargets[category]

  const finalTargets = (targets ?? {}) as Record<string, Co2ReferenceObject[]>

  // ✅ Guard: skip if missing and not manufacturing
  if (!finalTargets[subCategory] && subCategory !== 'manufacturing') return

  // ✅ Build updated item cleanly
  let finalItem: Partial<Co2ReferenceObject> = {}

  if (source === 'sustamize') {
    finalItem = {
      name: item.name,
      sid: item.sid,
      context: item.context,
      emissionType: item.emissionType,
      params: item.params,
      calculatedEntityEmission: item.calculatedEntityEmission,
      entityEmissionReference: item.entityEmissionReference,
    }
  } else if (source === 'custom') {
    const customParams = { ...item.params } as Record<string, any>
    delete customParams.countryCode
    finalItem = {
      name: item.name,
      comment: item.comment,
      context: item.context,
      emissionType: item.emissionType,
      unit: item.unit,
      params: customParams,
      calculatedEntityEmission: item.calculatedEntityEmission,
      entityEmissionReference: item.entityEmissionReference,
    }
  } else if (subCategory === 'Production Line') {
    item?.energy?.forEach((energyItem: any) => {
      delete energyItem.energyType
    })

    item?.employees?.forEach((employeeItem: any) => {
      delete employeeItem?.energyType
      delete employeeItem?.calculatedEntityEmission
      delete employeeItem?.entityEmissionReference
      delete employeeItem?.source
    })

    item?.equipment?.forEach((equipmentItem: any) => {
      delete equipmentItem?.energyType
      delete equipmentItem?.calculatedEntityEmission
      delete equipmentItem?.entityEmissionReference
      delete equipmentItem?.source
    })

    finalItem = {
      name: item.name,
      comment: item.comment,
      baseParams: item.baseParams,
      employees: item.employees,
      equipment: item.equipment,
      energy: item.energy,
    }
  }

  // ✅ Update logic
  if (source === 'custom' && subCategory === 'Production Line') {
    step.customTargets.production = finalItem as any
  } else if (finalTargets[subCategory]) {
    finalTargets[subCategory][itemIndex] = finalItem as Co2ReferenceObject
  }


    },
upsertCategoryItem: (
  state,
  action: PayloadAction<{
    stepIndex: number
    category: 'materials' | 'logistics' | 'production' | 'components'
    subCategory: string
    source: 'sustamize' | 'collector' | 'custom'

    prevSource?: 'sustamize' | 'collector' | 'custom'
    prevCategory?: 'materials' | 'logistics' | 'production' | 'components'
    prevSubCategory?: string
    prevStepIndex?: number

    itemIndex?: number
    item: AssemblyCo2ReferenceObject
  }>
) => {
  const {
    stepIndex, category, subCategory, source,
    prevSource, prevCategory, prevSubCategory, prevStepIndex,
    itemIndex, item,
  } = action.payload

  const chain = state.assemblyModel.assemblyModel.calculationProcessChain
  const toStep = chain[stepIndex]
  if (!toStep) return

  // ✅ Ensure targets exist before accessing
  toStep.sustamizeTargets = toStep.sustamizeTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  }
  toStep.customTargets = toStep.customTargets ?? {
    materials: {},
    logistics: {},
    production: {},
    components: {},
  }

  // ✅ Helper to safely access target maps
  const getMap = (
    step: any,
    which: 'sustamize' | 'collector' | 'custom',
    cat: any
  ) => {
    // Make sure both target containers are initialized
    step.sustamizeTargets = step.sustamizeTargets ?? {
      materials: {},
      logistics: {},
      production: {},
      components: {},
    }
    step.customTargets = step.customTargets ?? {
      materials: {},
      logistics: {},
      production: {},
      components: {},
    }

    const container =
      which === 'custom' ? step.customTargets : step.sustamizeTargets

    if (!container[cat]) container[cat] = {}
    return container[cat] as Record<string, Co2ReferenceObject[]>
  }

  // ✅ Determine if this is a true upsert or a move
  const sameBucket =
    prevSource !== undefined &&
    prevSource === source &&
    (prevStepIndex ?? stepIndex) === stepIndex &&
    prevCategory === category &&
    prevSubCategory === subCategory

  const normalized = normalizeItemForSource(item, source, category, subCategory)

  // -------- CASE A: TRUE UPSERT (same place, just replace)
  if (sameBucket) {
    const map = getMap(toStep, source, category)
    if (!map[subCategory]) map[subCategory] = []
    const arr = map[subCategory]

    if (itemIndex !== undefined && arr[itemIndex]) {
      arr[itemIndex] = normalized
    } else {
      const idx = arr.findIndex(
        (x) =>
          (normalized.sid && x.sid === normalized.sid) ||
          x.name === normalized.name
      )
      if (idx >= 0) arr[idx] = normalized
      else arr.push(normalized)
    }
    return
  }

  // -------- CASE B: MOVE (remove from previous, insert into new)
  if (prevSource && prevCategory && prevSubCategory !== undefined) {
    const fromStep = chain[prevStepIndex ?? stepIndex]
    if (fromStep) {
      fromStep.sustamizeTargets = fromStep.sustamizeTargets ?? {
        materials: {},
        logistics: {},
        production: {},
        components: {},
      }
      fromStep.customTargets = fromStep.customTargets ?? {
        materials: {},
        logistics: {},
        production: {},
        components: {},
      }

      if (prevSubCategory === 'Production Line' && prevSource === 'custom') {
        fromStep.customTargets.production = {} as any
      } else {
        const prevMap = getMap(fromStep, prevSource, prevCategory)
        const prevArr = prevMap[prevSubCategory]
        if (Array.isArray(prevArr)) {
          const removeIdx =
            itemIndex ??
            prevArr.findIndex(
              (x) =>
                (item.sid && x.sid === item.sid) || x.name === item.name
            )
          if (removeIdx >= 0) prevArr.splice(removeIdx, 1)
        }
      }
    }
  }

  // -------- Insert into the new target (de-dup)
  if (subCategory === 'Production Line' && source === 'custom') {
    toStep.customTargets.production = normalized
  } else {
    const nextMap = getMap(toStep, source, category)
    if (!nextMap[subCategory]) nextMap[subCategory] = []
    const nextArr = nextMap[subCategory]
    const existsIdx = nextArr.findIndex(
      (x) =>
        (normalized.sid && x.sid === normalized.sid) ||
        x.name === normalized.name
    )
    if (existsIdx >= 0) nextArr[existsIdx] = normalized
    else nextArr.push(normalized)
  }
}
  },
});

// Export your new actions:
export const {
  setassemblyModel,
  addCalculationProcessStep,
  deleteCalculationProcessStep,
  addCategoryItem,
  removeCategoryItem,
  updateCategoryItem,
  upsertCategoryItem,
  renameCalculationProcessStep,
  addCalculationProcessStepFromTemplate
} = assemblyModelSlice.actions;

export default assemblyModelSlice.reducer;

export const assemblyModelReducerState = (state: RootState) => state.assemblyModel;
